import React, { useState, useMemo } from 'react';
import {
  View,
  StyleSheet,
  ScrollView,
  Platform,
  TouchableOpacity,
} from 'react-native';
import { AgGridReact } from 'ag-grid-react';
import {
  ColDef,
  GridOptions,
  ModuleRegistry,
  AllCommunityModule,
} from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import Text from '../../../components/common/Text';
import { useTheme } from '../../../hooks/useTheme';
import { palette } from '../../../utils/palette';
import {
  useRolePermissions,
  useSubscriptionPermissions,
  useInitializePermissions,
} from '../../../hooks/useAdminData';
import { Ionicons } from '@expo/vector-icons';
import { TouchableOpacity as RNTouchableOpacity } from 'react-native';

// Register AG Grid modules
ModuleRegistry.registerModules([AllCommunityModule]);

interface Permission {
  id: number;
  name: string;
  description?: string;
  resource: string;
  action: string;
  created_at?: string;
  updated_at?: string;
}

interface RolePermission {
  role: string;
  permissions: Permission[];
}

interface SubscriptionPermission {
  permission_name: string;
  description?: string;
  resource: string;
  action: string;
  endpoint?: string;
  method?: string;
}

interface SubscriptionPermissions {
  subscription_plan: string;
  permissions: SubscriptionPermission[];
}

const PermissionsView = () => {
  const { colors } = useTheme();
  const [activeTab, setActiveTab] = useState<'roles' | 'subscriptions'>(
    'roles'
  );

  const {
    data: rolePermissionsData,
    isLoading: rolesLoading,
    error: rolesError,
  } = useRolePermissions({
    enabled: true, // Always load, not just when tab is active
  });

  const {
    data: subscriptionPermissionsData,
    isLoading: subscriptionsLoading,
    error: subscriptionsError,
  } = useSubscriptionPermissions({
    enabled: true, // Always load, not just when tab is active
  });

  const initializePermissions = useInitializePermissions();

  const rolePermissions: RolePermission[] = rolePermissionsData?.roles || [];
  const subscriptionPermissions: SubscriptionPermissions[] =
    subscriptionPermissionsData || [];

  const hasNoRolePermissions =
    rolePermissions.length === 0 ||
    rolePermissions.every((rp) => rp.permissions.length === 0);
  const _hasNoSubscriptionPermissions =
    subscriptionPermissions.length === 0 ||
    subscriptionPermissions.every((sp) => sp.permissions.length === 0);

  // Helper function to safely extract error message
  const getErrorMessage = (error: any): string => {
    if (!error) return 'Unknown error';
    if (typeof error === 'string') return error;
    if (error.message) return error.message;
    if (error.detail) return error.detail;
    if (error.error)
      return typeof error.error === 'string'
        ? error.error
        : JSON.stringify(error.error);
    return JSON.stringify(error);
  };

  // Debug logging
  React.useEffect(() => {
    console.log('Role Permissions Data:', rolePermissionsData);
    console.log('Subscription Permissions Data:', subscriptionPermissionsData);
    console.log('Roles Error:', rolesError);
    console.log('Subscriptions Error:', subscriptionsError);
    if (rolesError) {
      console.error(
        'Roles Error Details:',
        JSON.stringify(rolesError, null, 2)
      );
    }
    if (subscriptionsError) {
      console.error(
        'Subscriptions Error Details:',
        JSON.stringify(subscriptionsError, null, 2)
      );
    }
  }, [
    rolePermissionsData,
    subscriptionPermissionsData,
    rolesError,
    subscriptionsError,
  ]);

  // Role Permissions Grid Columns
  const roleColumns: ColDef[] = useMemo(
    () => [
      {
        field: 'role',
        headerName: 'Role',
        width: 120,
        pinned: 'left',
        sortable: true,
        filter: true,
        cellRenderer: (params: any) => {
          const role = params.value || 'user';
          const roleColors: { [key: string]: string } = {
            admin: palette.brightGreen,
            moderator: palette.lightOrange,
            user: palette.darkGrayBlue,
          };
          return (
            <span
              style={{
                padding: '2px 8px',
                borderRadius: '4px',
                backgroundColor: `${roleColors[role] || roleColors.user}20`,
                color: roleColors[role] || roleColors.user,
                fontSize: '11px',
                fontWeight: '600',
              }}
            >
              {role.toUpperCase()}
            </span>
          );
        },
      },
      {
        field: 'permission_name',
        headerName: 'Permission',
        flex: 2,
        sortable: true,
        filter: true,
      },
      {
        field: 'description',
        headerName: 'Description',
        flex: 2,
        sortable: true,
        filter: true,
      },
      {
        field: 'resource',
        headerName: 'Resource',
        width: 150,
        sortable: true,
        filter: true,
      },
      {
        field: 'action',
        headerName: 'Action',
        width: 120,
        sortable: true,
        filter: true,
      },
    ],
    []
  );

  // Subscription Permissions Grid Columns
  const subscriptionColumns: ColDef[] = useMemo(
    () => [
      {
        field: 'subscription_plan',
        headerName: 'Plan',
        width: 150,
        pinned: 'left',
        sortable: true,
        filter: true,
        cellRenderer: (params: any) => {
          const plan = params.value || 'free';
          const planColors: { [key: string]: string } = {
            free: palette.darkGrayBlue,
            freemium_trial: palette.brightBlue,
            pro: palette.cyanLimeGreen,
            pay_per_scan: palette.lightOrange,
          };
          const displayName = plan
            .replace('_', ' ')
            .replace(/\b\w/g, (l: string) => l.toUpperCase());
          return (
            <span
              style={{
                padding: '2px 8px',
                borderRadius: '4px',
                backgroundColor: `${planColors[plan] || planColors.free}20`,
                color: planColors[plan] || planColors.free,
                fontSize: '11px',
                fontWeight: '600',
              }}
            >
              {displayName}
            </span>
          );
        },
      },
      {
        field: 'permission_name',
        headerName: 'Permission',
        flex: 2,
        sortable: true,
        filter: true,
      },
      {
        field: 'description',
        headerName: 'Description',
        flex: 2,
        sortable: true,
        filter: true,
      },
      {
        field: 'resource',
        headerName: 'Resource',
        width: 150,
        sortable: true,
        filter: true,
      },
      {
        field: 'action',
        headerName: 'Action',
        width: 120,
        sortable: true,
        filter: true,
      },
      {
        field: 'endpoint',
        headerName: 'Endpoint',
        flex: 1,
        sortable: true,
        filter: true,
      },
      {
        field: 'method',
        headerName: 'Method',
        width: 100,
        sortable: true,
        filter: true,
        cellRenderer: (params: any) => {
          const method = params.value || '';
          const methodColors: { [key: string]: string } = {
            GET: palette.brightBlue,
            POST: palette.cyanLimeGreen,
            PUT: palette.lightOrange,
            PATCH: palette.lightOrange,
            DELETE: palette.brightMatteRed,
          };
          return (
            <span
              style={{
                padding: '2px 6px',
                borderRadius: '4px',
                backgroundColor: `${methodColors[method] || palette.darkGrayBlue}20`,
                color: methodColors[method] || palette.darkGrayBlue,
                fontSize: '10px',
                fontWeight: '600',
                fontFamily: 'monospace',
              }}
            >
              {method}
            </span>
          );
        },
      },
    ],
    []
  );

  // Flatten role permissions for grid
  const rolePermissionsRows = rolePermissions.flatMap((rolePerm) =>
    rolePerm.permissions.map((perm) => ({
      role: rolePerm.role,
      permission_name: perm.name,
      description: perm.description || '',
      resource: perm.resource,
      action: perm.action,
    }))
  );

  // Flatten subscription permissions for grid
  const subscriptionPermissionsRows = subscriptionPermissions.flatMap(
    (subPerm) =>
      subPerm.permissions.map((perm) => ({
        subscription_plan: subPerm.subscription_plan,
        permission_name: perm.permission_name,
        description: perm.description || '',
        resource: perm.resource,
        action: perm.action,
        endpoint: perm.endpoint || '',
        method: perm.method || '',
      }))
  );

  const gridOptions: GridOptions = useMemo(
    () => ({
      theme: 'legacy',
      defaultColDef: {
        resizable: true,
        sortable: true,
        filter: true,
      },
      pagination: false,
      rowHeight: 50,
      headerHeight: 40,
      suppressCellFocus: true,
      animateRows: true,
      suppressPaginationPanel: true,
    }),
    []
  );

  if (Platform.OS !== 'web') {
    return (
      <ScrollView
        style={[styles.container, { backgroundColor: colors.background }]}
      >
        <View style={styles.tabContainer}>
          <View style={styles.tabButtons}>
            <TouchableOpacity
              style={[
                styles.tabButton,
                activeTab === 'roles' && { backgroundColor: colors.primary },
              ]}
              onPress={() => setActiveTab('roles')}
            >
              <Text
                style={[
                  styles.tabButtonText,
                  {
                    color: activeTab === 'roles' ? palette.white : colors.text,
                  },
                ]}
              >
                Roles
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[
                styles.tabButton,
                activeTab === 'subscriptions' && {
                  backgroundColor: colors.primary,
                },
              ]}
              onPress={() => setActiveTab('subscriptions')}
            >
              <Text
                style={[
                  styles.tabButtonText,
                  {
                    color:
                      activeTab === 'subscriptions'
                        ? palette.white
                        : colors.text,
                  },
                ]}
              >
                Subscriptions
              </Text>
            </TouchableOpacity>
          </View>
        </View>

        {activeTab === 'roles' && (
          <View style={styles.mobileView}>
            {rolesLoading ? (
              <Text style={[styles.loadingText, { color: colors.text }]}>
                Loading...
              </Text>
            ) : rolesError ? (
              <View style={styles.errorContainer}>
                <Text
                  style={[
                    styles.errorText,
                    { color: colors.error || palette.brightMatteRed },
                  ]}
                >
                  Error loading role permissions: {getErrorMessage(rolesError)}
                </Text>
                <RNTouchableOpacity
                  style={[
                    styles.initButton,
                    { backgroundColor: colors.primary, marginTop: 16 },
                  ]}
                  onPress={() => initializePermissions.mutate()}
                  disabled={initializePermissions.isPending}
                >
                  <Text
                    style={[styles.initButtonText, { color: palette.white }]}
                  >
                    {initializePermissions.isPending
                      ? 'Initializing...'
                      : 'Try Initialize Permissions'}
                  </Text>
                </RNTouchableOpacity>
              </View>
            ) : hasNoRolePermissions ? (
              <View style={styles.emptyContainer}>
                <Text style={[styles.emptyText, { color: colors.secondary }]}>
                  No role permissions found. Permissions may need to be
                  initialized.
                </Text>
                <RNTouchableOpacity
                  style={[
                    styles.initButton,
                    { backgroundColor: colors.primary },
                  ]}
                  onPress={() => initializePermissions.mutate()}
                  disabled={initializePermissions.isPending}
                >
                  <Text
                    style={[styles.initButtonText, { color: palette.white }]}
                  >
                    {initializePermissions.isPending
                      ? 'Initializing...'
                      : 'Initialize Permissions'}
                  </Text>
                </RNTouchableOpacity>
              </View>
            ) : (
              rolePermissions.map((rolePerm) => (
                <View
                  key={rolePerm.role}
                  style={[
                    styles.roleCard,
                    {
                      backgroundColor: colors.card,
                      borderColor: colors.border,
                    },
                  ]}
                >
                  <Text style={[styles.roleTitle, { color: colors.primary }]}>
                    {rolePerm.role.toUpperCase()}
                  </Text>
                  <Text
                    style={[
                      styles.permissionCount,
                      { color: colors.secondary },
                    ]}
                  >
                    {rolePerm.permissions.length} permissions
                  </Text>
                  {rolePerm.permissions.map((perm) => (
                    <View key={perm.id} style={styles.permissionItem}>
                      <Text
                        style={[styles.permissionName, { color: colors.text }]}
                      >
                        {perm.name}
                      </Text>
                      {perm.description && (
                        <Text
                          style={[
                            styles.permissionDesc,
                            { color: colors.secondary },
                          ]}
                        >
                          {perm.description}
                        </Text>
                      )}
                      <View style={styles.permissionMeta}>
                        <Text
                          style={[
                            styles.permissionMetaText,
                            { color: colors.secondary },
                          ]}
                        >
                          {perm.resource} • {perm.action}
                        </Text>
                      </View>
                    </View>
                  ))}
                </View>
              ))
            )}
          </View>
        )}

        {activeTab === 'subscriptions' && (
          <View style={styles.mobileView}>
            {subscriptionsLoading ? (
              <Text style={[styles.loadingText, { color: colors.text }]}>
                Loading...
              </Text>
            ) : subscriptionsError ? (
              <View style={styles.errorContainer}>
                <Text
                  style={[
                    styles.errorText,
                    { color: colors.error || palette.brightMatteRed },
                  ]}
                >
                  Error loading subscription permissions:{' '}
                  {getErrorMessage(subscriptionsError)}
                </Text>
              </View>
            ) : subscriptionPermissions.length === 0 ? (
              <View style={styles.emptyContainer}>
                <Text style={[styles.emptyText, { color: colors.secondary }]}>
                  No subscription permissions found.
                </Text>
              </View>
            ) : (
              subscriptionPermissions.map((subPerm) => (
                <View
                  key={subPerm.subscription_plan}
                  style={[
                    styles.roleCard,
                    {
                      backgroundColor: colors.card,
                      borderColor: colors.border,
                    },
                  ]}
                >
                  <Text style={[styles.roleTitle, { color: colors.primary }]}>
                    {subPerm.subscription_plan.toUpperCase().replace('_', ' ')}
                  </Text>
                  <Text
                    style={[
                      styles.permissionCount,
                      { color: colors.secondary },
                    ]}
                  >
                    {subPerm.permissions.length} permissions
                  </Text>
                  {subPerm.permissions.map((perm, idx) => (
                    <View key={idx} style={styles.permissionItem}>
                      <Text
                        style={[styles.permissionName, { color: colors.text }]}
                      >
                        {perm.permission_name}
                      </Text>
                      {perm.description && (
                        <Text
                          style={[
                            styles.permissionDesc,
                            { color: colors.secondary },
                          ]}
                        >
                          {perm.description}
                        </Text>
                      )}
                      <View style={styles.permissionMeta}>
                        <Text
                          style={[
                            styles.permissionMetaText,
                            { color: colors.secondary },
                          ]}
                        >
                          {perm.resource} • {perm.action}
                        </Text>
                        {perm.endpoint && (
                          <Text
                            style={[
                              styles.permissionMetaText,
                              { color: colors.secondary },
                            ]}
                          >
                            {perm.method} {perm.endpoint}
                          </Text>
                        )}
                      </View>
                    </View>
                  ))}
                </View>
              ))
            )}
          </View>
        )}
      </ScrollView>
    );
  }

  return (
    <View style={[styles.container, { backgroundColor: colors.background }]}>
      <View style={styles.tabContainer}>
        <View style={styles.tabButtons}>
          <TouchableOpacity
            style={[
              styles.tabButton,
              activeTab === 'roles' && { backgroundColor: colors.primary },
            ]}
            onPress={() => setActiveTab('roles')}
          >
            <Ionicons
              name="people"
              size={16}
              color={activeTab === 'roles' ? palette.white : colors.text}
              style={{ marginRight: 8 }}
            />
            <Text
              style={[
                styles.tabButtonText,
                { color: activeTab === 'roles' ? palette.white : colors.text },
              ]}
            >
              Roles
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[
              styles.tabButton,
              activeTab === 'subscriptions' && {
                backgroundColor: colors.primary,
              },
            ]}
            onPress={() => setActiveTab('subscriptions')}
          >
            <Ionicons
              name="card"
              size={16}
              color={
                activeTab === 'subscriptions' ? palette.white : colors.text
              }
              style={{ marginRight: 8 }}
            />
            <Text
              style={[
                styles.tabButtonText,
                {
                  color:
                    activeTab === 'subscriptions' ? palette.white : colors.text,
                },
              ]}
            >
              Subscriptions
            </Text>
          </TouchableOpacity>
        </View>
      </View>

      <View style={styles.gridContainer}>
        {activeTab === 'roles' && (
          <>
            {rolesLoading ? (
              <View style={styles.loadingContainer}>
                <Text style={[styles.loadingText, { color: colors.text }]}>
                  Loading role permissions...
                </Text>
              </View>
            ) : rolesError ? (
              <View style={styles.errorContainer}>
                <Text
                  style={[
                    styles.errorText,
                    { color: colors.error || palette.brightMatteRed },
                  ]}
                >
                  Error loading role permissions: {getErrorMessage(rolesError)}
                </Text>
                <RNTouchableOpacity
                  style={[
                    styles.initButton,
                    { backgroundColor: colors.primary, marginTop: 16 },
                  ]}
                  onPress={() => initializePermissions.mutate()}
                  disabled={initializePermissions.isPending}
                >
                  <Text
                    style={[styles.initButtonText, { color: palette.white }]}
                  >
                    {initializePermissions.isPending
                      ? 'Initializing...'
                      : 'Try Initialize Permissions'}
                  </Text>
                </RNTouchableOpacity>
              </View>
            ) : hasNoRolePermissions ? (
              <View style={styles.emptyContainer}>
                <Text style={[styles.emptyText, { color: colors.secondary }]}>
                  No role permissions found. Permissions may need to be
                  initialized.
                </Text>
                <RNTouchableOpacity
                  style={[
                    styles.initButton,
                    { backgroundColor: colors.primary },
                  ]}
                  onPress={() => initializePermissions.mutate()}
                  disabled={initializePermissions.isPending}
                >
                  <Text
                    style={[styles.initButtonText, { color: palette.white }]}
                  >
                    {initializePermissions.isPending
                      ? 'Initializing...'
                      : 'Initialize Permissions'}
                  </Text>
                </RNTouchableOpacity>
              </View>
            ) : (
              <div
                className="ag-theme-alpine"
                style={
                  {
                    height: 'calc(100vh - 200px)',
                    width: '100%',
                    overflow: 'hidden',
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    '--ag-header-background-color': colors.card,
                    '--ag-background-color': colors.background,
                    '--ag-odd-row-background-color': colors.card,
                    '--ag-row-hover-color': colors.card,
                    '--ag-header-foreground-color': colors.text,
                    '--ag-foreground-color': colors.text,
                    '--ag-border-color': colors.border,
                    '--ag-secondary-border-color': colors.border,
                    '--ag-input-focus-border-color': colors.primary,
                    '--ag-selected-row-background-color': `${colors.primary}20`,
                    '--ag-range-selection-background-color': `${colors.primary}15`,
                    '--ag-range-selection-border-color': colors.primary,
                    '--ag-checkbox-checked-color': colors.primary,
                    '--ag-checkbox-unchecked-color': colors.border,
                    '--ag-input-disabled-background-color': colors.card,
                    '--ag-input-disabled-border-color': colors.border,
                    '--ag-input-disabled-foreground-color': colors.secondary,
                    '--ag-panel-background-color': colors.card,
                    '--ag-tool-panel-background-color': colors.card,
                    '--ag-side-button-selected-background-color':
                      colors.primary,
                    '--ag-side-button-selected-foreground-color': palette.white,
                  } as React.CSSProperties & { [key: string]: any }
                }
              >
                <style>{`
                  .ag-theme-alpine .ag-body-viewport::-webkit-scrollbar,
                  .ag-theme-alpine .ag-body-horizontal-scroll::-webkit-scrollbar {
                    display: none;
                  }
                  .ag-theme-alpine .ag-body-viewport,
                  .ag-theme-alpine .ag-body-horizontal-scroll {
                    -ms-overflow-style: none;
                    scrollbar-width: none;
                  }
                  .ag-theme-alpine {
                    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
                    font-size: 12px;
                  }
                  .ag-theme-alpine .ag-header {
                    border-bottom: 1px solid ${colors.border};
                    font-size: 13px;
                    font-weight: 600;
                  }
                  .ag-theme-alpine .ag-header-cell {
                    border-right: 1px solid ${colors.border};
                    font-size: 13px;
                    font-weight: 600;
                  }
                  .ag-theme-alpine .ag-header-cell-text {
                    font-size: 13px;
                    font-weight: 600;
                  }
                  .ag-theme-alpine .ag-row {
                    border-bottom: 1px solid ${colors.border};
                    font-size: 12px;
                    font-weight: 400;
                  }
                  .ag-theme-alpine .ag-cell {
                    border-right: 1px solid ${colors.border};
                    font-size: 12px;
                    font-weight: 400;
                    line-height: 1.5;
                  }
                  .ag-theme-alpine .ag-cell-value {
                    font-size: 12px;
                    font-weight: 400;
                  }
                  .ag-theme-alpine input,
                  .ag-theme-alpine select,
                  .ag-theme-alpine button {
                    font-size: 12px;
                    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
                  }
                `}</style>
                <AgGridReact
                  rowData={rolePermissionsRows}
                  columnDefs={roleColumns}
                  gridOptions={gridOptions}
                  domLayout="normal"
                />
              </div>
            )}
          </>
        )}

        {activeTab === 'subscriptions' && (
          <>
            {subscriptionsLoading ? (
              <View style={styles.loadingContainer}>
                <Text style={[styles.loadingText, { color: colors.text }]}>
                  Loading subscription permissions...
                </Text>
              </View>
            ) : subscriptionsError ? (
              <View style={styles.errorContainer}>
                <Text
                  style={[
                    styles.errorText,
                    { color: colors.error || palette.brightMatteRed },
                  ]}
                >
                  Error loading subscription permissions:{' '}
                  {getErrorMessage(subscriptionsError)}
                </Text>
              </View>
            ) : subscriptionPermissionsRows.length === 0 ? (
              <View style={styles.emptyContainer}>
                <Text style={[styles.emptyText, { color: colors.secondary }]}>
                  No subscription permissions found.
                </Text>
              </View>
            ) : (
              <div
                className="ag-theme-alpine"
                style={
                  {
                    height: 'calc(100vh - 200px)',
                    width: '100%',
                    overflow: 'hidden',
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    '--ag-header-background-color': colors.card,
                    '--ag-background-color': colors.background,
                    '--ag-odd-row-background-color': colors.card,
                    '--ag-row-hover-color': colors.card,
                    '--ag-header-foreground-color': colors.text,
                    '--ag-foreground-color': colors.text,
                    '--ag-border-color': colors.border,
                    '--ag-secondary-border-color': colors.border,
                    '--ag-input-focus-border-color': colors.primary,
                    '--ag-selected-row-background-color': `${colors.primary}20`,
                    '--ag-range-selection-background-color': `${colors.primary}15`,
                    '--ag-range-selection-border-color': colors.primary,
                    '--ag-checkbox-checked-color': colors.primary,
                    '--ag-checkbox-unchecked-color': colors.border,
                    '--ag-input-disabled-background-color': colors.card,
                    '--ag-input-disabled-border-color': colors.border,
                    '--ag-input-disabled-foreground-color': colors.secondary,
                    '--ag-panel-background-color': colors.card,
                    '--ag-tool-panel-background-color': colors.card,
                    '--ag-side-button-selected-background-color':
                      colors.primary,
                    '--ag-side-button-selected-foreground-color': palette.white,
                  } as React.CSSProperties & { [key: string]: any }
                }
              >
                <style>{`
                  .ag-theme-alpine .ag-body-viewport::-webkit-scrollbar,
                  .ag-theme-alpine .ag-body-horizontal-scroll::-webkit-scrollbar {
                    display: none;
                  }
                  .ag-theme-alpine .ag-body-viewport,
                  .ag-theme-alpine .ag-body-horizontal-scroll {
                    -ms-overflow-style: none;
                    scrollbar-width: none;
                  }
                  .ag-theme-alpine {
                    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
                    font-size: 12px;
                  }
                  .ag-theme-alpine .ag-header {
                    border-bottom: 1px solid ${colors.border};
                    font-size: 13px;
                    font-weight: 600;
                  }
                  .ag-theme-alpine .ag-header-cell {
                    border-right: 1px solid ${colors.border};
                    font-size: 13px;
                    font-weight: 600;
                  }
                  .ag-theme-alpine .ag-header-cell-text {
                    font-size: 13px;
                    font-weight: 600;
                  }
                  .ag-theme-alpine .ag-row {
                    border-bottom: 1px solid ${colors.border};
                    font-size: 12px;
                    font-weight: 400;
                  }
                  .ag-theme-alpine .ag-cell {
                    border-right: 1px solid ${colors.border};
                    font-size: 12px;
                    font-weight: 400;
                    line-height: 1.5;
                  }
                  .ag-theme-alpine .ag-cell-value {
                    font-size: 12px;
                    font-weight: 400;
                  }
                  .ag-theme-alpine input,
                  .ag-theme-alpine select,
                  .ag-theme-alpine button {
                    font-size: 12px;
                    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
                  }
                `}</style>
                <AgGridReact
                  rowData={subscriptionPermissionsRows}
                  columnDefs={subscriptionColumns}
                  gridOptions={gridOptions}
                  domLayout="normal"
                />
              </div>
            )}
          </>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 16,
  },
  tabContainer: {
    marginBottom: 16,
  },
  tabButtons: {
    flexDirection: 'row',
    gap: 8,
  },
  tabButton: {
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 8,
    backgroundColor: palette.veryLightGray,
    flexDirection: 'row',
    alignItems: 'center',
  },
  tabButtonText: {
    fontSize: 14,
    fontWeight: '600',
  },
  gridContainer: {
    flex: 1,
    width: '100%',
    minHeight: 400,
  },
  mobileView: {
    gap: 16,
  },
  roleCard: {
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    marginBottom: 16,
  },
  roleTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  permissionCount: {
    fontSize: 12,
    marginBottom: 12,
  },
  permissionItem: {
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: palette.lightGray,
  },
  permissionName: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 4,
  },
  permissionDesc: {
    fontSize: 12,
    marginBottom: 4,
  },
  permissionMeta: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 4,
  },
  permissionMetaText: {
    fontSize: 11,
  },
  loadingText: {
    textAlign: 'center',
    padding: 20,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
  },
  errorText: {
    textAlign: 'center',
    fontSize: 14,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
  },
  emptyText: {
    textAlign: 'center',
    fontSize: 14,
    marginBottom: 16,
  },
  initButton: {
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
    marginTop: 16,
  },
  initButtonText: {
    fontSize: 14,
    fontWeight: '600',
    textAlign: 'center',
  },
});

export default PermissionsView;
